import { z } from "zod";
import { ClassFilterType } from "@common/types/class";
import { NativeInvoiceTermType } from "@common/helpers/invoice-terms.helper";
import { InvoiceSource } from "@common/types/invoice";
import { ReportFormat } from "@common/helpers/reports";
import { withRequiredPresentation } from "@common/schemas/domains/reportSettings/report-presentation.schema";
import {
    ArAgingBucket,
    ArAgingDetailSource,
} from "@common/types/reports/ar-aging";

// Shared filters for both Summary and Detail, and for the Summary->Detail
// drilldown. `asOfDate` defaults to today when omitted (resolved server-side).
// `entityIds` is multi-select like the GL; only accrual entities are honored.
export const arAgingReportQuery = z.object({
    asOfDate: z.string().date().optional(),
    entityIds: z.array(z.coerce.number().int().positive()).min(1),
    classes: z.array(z.string()).optional(),
    classFilterType: z.nativeEnum(ClassFilterType).optional(),
    // UUID = one customer ($0-open still returns a row). null = unassigned
    // invoices only (Summary "No customer" drill-down). Omitted = every customer.
    customerId: z.string().uuid().nullable().optional(),
    termTypes: z.array(z.nativeEnum(NativeInvoiceTermType)).optional(),
    // Account filter — mirrors the invoices page: `accountIds` selects
    // invoices tied to a connected financial account; `invoiceSources`
    // selects by source instead (Kick/MANUAL has no financialAccountId).
    accountIds: z.array(z.coerce.number().int().positive()).optional(),
    invoiceSources: z.array(z.nativeEnum(InvoiceSource)).optional(),
    createdOnFrom: z.string().date().optional(),
    createdOnTo: z.string().date().optional(),
    dueDateFrom: z.string().date().optional(),
    dueDateTo: z.string().date().optional(),
});

export const arAgingSummaryQuery = arAgingReportQuery;

// Drilldown-only params, layered on top of the shared query for Detail.
export const arAgingDetailQuery = arAgingReportQuery.extend({
    // Scopes Detail to just the selected buckets' sections (multi-select filter,
    // and also how a Summary drilldown click scopes to one bucket via a
    // single-element array). Omitted/empty = every bucket with rows (Customer
    // Total, grand total, standalone Detail).
    buckets: z.array(z.nativeEnum(ArAgingBucket)).optional(),
});

export const arAgingReportExportQuery = arAgingReportQuery.extend({
    format: z.nativeEnum(ReportFormat).optional(),
});

export const arAgingDetailExportQuery = arAgingDetailQuery.extend({
    format: z.nativeEnum(ReportFormat).optional(),
});

// Exhaustive object (not z.record) so every one of the 5 fixed buckets is
// always present on the wire — the Summary contract requires all bucket
// columns to render even when $0 (AR-9).
export const arAgingBucketAmountsSchema = z.object({
    [ArAgingBucket.CURRENT]: z.number(),
    [ArAgingBucket.DAYS_1_TO_30]: z.number(),
    [ArAgingBucket.DAYS_31_TO_60]: z.number(),
    [ArAgingBucket.DAYS_61_TO_90]: z.number(),
    [ArAgingBucket.DAYS_91_AND_OVER]: z.number(),
});

export const arAgingSummaryRowSchema = z.object({
    customerId: z.string().uuid().nullable(),
    customerName: z.string(),
    amountsByBucket: arAgingBucketAmountsSchema,
    total: z.number(),
});

export const arAgingSummaryResponseSchema = z.object({
    asOfDate: z.string(),
    rows: z.array(arAgingSummaryRowSchema),
    totalsByBucket: arAgingBucketAmountsSchema,
    grandTotal: z.number(),
});

export const arAgingSummaryResponseWithPresentationSchema =
    withRequiredPresentation(arAgingSummaryResponseSchema);

export const arAgingDetailRowSchema = z.object({
    invoiceId: z.string().uuid(),
    date: z.string(),
    source: z.nativeEnum(ArAgingDetailSource),
    reference: z.string().nullable(),
    customerId: z.string().uuid().nullable(),
    customerName: z.string(),
    description: z.string().nullable(),
    classId: z.string().nullable(),
    className: z.string().nullable(),
    dueDate: z.string().nullable(),
    entityId: z.number(),
    entityName: z.string(),
    amount: z.number(),
    open: z.number(),
    bucket: z.nativeEnum(ArAgingBucket),
});

export const arAgingDetailBucketSectionSchema = z.object({
    bucket: z.nativeEnum(ArAgingBucket),
    rows: z.array(arAgingDetailRowSchema),
    subtotalAmount: z.number(),
    subtotalOpen: z.number(),
});

export const arAgingDetailResponseSchema = z.object({
    asOfDate: z.string(),
    showEntityColumn: z.boolean(),
    sections: z.array(arAgingDetailBucketSectionSchema),
    grandTotalAmount: z.number(),
    grandTotalOpen: z.number(),
});

export const arAgingDetailResponseWithPresentationSchema =
    withRequiredPresentation(arAgingDetailResponseSchema);

export type ArAgingReportQuery = z.infer<typeof arAgingReportQuery>;
export type ArAgingDetailQuery = z.infer<typeof arAgingDetailQuery>;
